import type { Customer } from "./data";

export type CustomerFilters = {
  dealer: string[];
  municipality: string[];
  assignee: string[];
  /** Only `"disconnected"` today: the design filters on no connection alone. */
  opsCenter: string[];
};

export const EMPTY_FILTERS: CustomerFilters = {
  dealer: [],
  municipality: [],
  assignee: [],
  opsCenter: [],
};

/** Facet names in the order the drawer renders them. */
export const FACETS = [
  "dealer",
  "municipality",
  "assignee",
  "opsCenter",
] as const;
export type Facet = (typeof FACETS)[number];

export const OPS_CENTER_OPTIONS = ["disconnected"] as const;

export function countFilters(filters: CustomerFilters) {
  return FACETS.reduce((total, facet) => total + filters[facet].length, 0);
}

/** Adds the value if absent, drops it if present. Never mutates the input. */
export function toggleFilter(
  filters: CustomerFilters,
  facet: Facet,
  value: string
): CustomerFilters {
  const current: string[] = filters[facet];
  const next = current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value];
  return { ...filters, [facet]: next };
}

/** What each facet reads off a client. One place, so a facet cannot drift. */
const VALUE_OF: Record<Facet, (customer: Customer) => string | undefined> = {
  dealer: (customer) => customer.dealer,
  municipality: (customer) => customer.municipality,
  assignee: (customer) => customer.assignee,
  opsCenter: (customer) =>
    customer.organizationId === null ? "disconnected" : "connected",
};

/**
 * An empty facet means "no constraint", not "match nothing" — otherwise the
 * first render, with nothing selected, would show an empty table. Facets are
 * ANDed with each other and ORed within themselves, which is what a reader
 * expects from a row of chips.
 */
export function filterCustomers(
  customers: Customer[],
  filters: CustomerFilters,
  search = ""
): Customer[] {
  const needle = search.trim().toLocaleLowerCase();
  return customers.filter((customer) => {
    for (const facet of FACETS) {
      const selected: string[] = filters[facet];
      const value = VALUE_OF[facet](customer);
      if (selected.length && !(value && selected.includes(value))) {
        return false;
      }
    }
    if (!needle) {
      return true;
    }
    // Searches what the row shows, so a hit is always visible to the reader.
    return [customer.name, customer.accountNumber, customer.municipality]
      .join(" ")
      .toLocaleLowerCase()
      .includes(needle);
  });
}
